import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Typography, Affix, App, Modal, Form, Space, Switch, Image, Avatar } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import { useNavigate, Link } from "react-router-dom"
import _ from "lodash"
import { FaPlus, FaEdit, FaImage, FaTrash } from "react-icons/fa"
import { API, Enum } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global"
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

const currency = new Intl.NumberFormat()
const { Pages: { HostManagement: { Host: HostLanguage } }, Enum: EnumLanguage, Share, Message: MessageLanguage } = language
const {  HOST_MANAGEMENT_HOST_PAGINATE, HOST_MANAGEMENT_HOST_REMOVE, HOST_MANAGEMENT_HOST_UPDATE, FILE_MANAGEMENT_FILE_DOWNLOAD } = API
const { Message } = Enum

interface FileType {
    id: string,
    originalName: string,
    fileName: string,
    mimeType: string,
    size: number,
}

interface HostType {
    id: string,
    title: string,
    description: string,
    host: string,
    smsTemplate: object,
    favicon: FileType,
    verticalLogo: FileType,
    horizontalLogo: FileType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

export default function Index() {

    const [formModal] = Form.useForm()
    const navigate = useNavigate()
    const { accessToken, baseURL } = useContext(GlobalContext)
    const [hosts, setHosts] = useState<HostType[]>([])
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    })
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp()

    const hostColumns: ColumnsType<HostType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: HostLanguage.horizontalLogo,
            dataIndex: "horizontalLogo",
            key: "horizontalLogo",
            render: (text, record) => text ? <Image width={100} height={100} src={baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + text.id + `?width=1000&height=1000&token=${accessToken}`} alt={text.originalName} /> : <Avatar shape="square" size={100} icon={<FaImage />} style={{lineHeight:2.5}} />
        },
        {
            title: HostLanguage.title,
            dataIndex: "title",
            key: "title",
            sorter: true,
        },
        {
            title: HostLanguage.description,
            dataIndex: "description",
            key: "description",
        },
        {
            title: HostLanguage.host,
            dataIndex: 'host',
            key: "host",
            sorter: true,
            className: 'text-align-left',
        },
        {
            title: Share.publish,
            dataIndex: 'publish',
            key: 'publish',
            render: (text, { id }) =>  (
                <Switch
                    checkedChildren={Share.active}
                    unCheckedChildren={Share.inactive}
                    checked={text}
                    style={{width:75}}
                    onClick={() => {
                        setLoadingRecords(true)
                        axios.patch(HOST_MANAGEMENT_HOST_UPDATE + id, { publish: !text }).then((response: {}) => {
                            message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                            const { current, pageSize } = pagination
                            getHosts({ page: current, limit: pageSize })
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error;
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response)
                            setLoadingRecords(false)
                        })
                    }}
                />
            )
        },
        {
            title: Share.edit,
            dataIndex: "edit",
            key: "edit",
            render: (text, record) =>  <Link to={'/host-management/host/edit/' + record.id}><FaEdit size={20} /></Link>
        },
        {
            title: Share.delete,
            dataIndex: "delete",
            key: "delete",
            render: (text, record) =>  (
                <Typography.Link onClick={() => modal.confirm({
                    title: Share.deleteConfirmTitle,
                    okText: Share.deleteConfirmOk,
                    okType: 'danger',
                    cancelText: Share.deleteConfirmCancel,
                    onOk() {
                        setLoadingRecords(true)
                        axios.delete(HOST_MANAGEMENT_HOST_REMOVE + record.id).then((response: {}) => {
                            const { total = 1, pageSize = 1, current = 1 } = pagination
                            const params = {
                                page: total % pageSize !== 1 ? current : current - 1,
                                limit: pageSize,
                            }
                            getHosts(params)
                            message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response )
                            setLoadingRecords(false)
                        })
                    }
                })}><FaTrash size={20} /></Typography.Link>
            )
        }
    ]

    const getHosts = (params: AntTableParams) => {
        setLoadingRecords(true)
        axios.post(HOST_MANAGEMENT_HOST_PAGINATE, null, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: hosts, page: current, limit: pageSize, totalDocs: total } } = response
            let row = (current - 1) * pageSize
            setHosts(hosts.map<HostType>((host: HostType) => ({ ...host, row: ++row, key: host.id })))
            setLoadingRecords(false)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        const { current, pageSize } = pagination
        getHosts({ page: current, limit: pageSize })
    }, [])

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filtersTable: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getHosts({ page: current, limit: pageSize, sort })
    }

    return (
        <>
            <Table
                columns={hostColumns}
                dataSource={hosts}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                    <Button key='add' type="primary" icon={<FaPlus />} style={{height:'40px'}} onClick={() => navigate('/host-management/host/add')}>{HostLanguage.addHost}</Button>
                </Space.Compact>
            </Affix>
        </>
    )
}
